import { Controller } from "@hotwired/stimulus"

// Caffeine Clearance Calculator (Phase 1 item 1.7).
//
// Pure-frontend. Models caffeine in the bloodstream as exponential decay:
//   C(t) = C0 * (0.5) ^ (t / halfLife)
//
// Inputs are a list of drinks (each with mg caffeine and intake time, in
// today's local time) plus a bedtime. We sum contributions across drinks
// to render an hourly decay curve, then back-solve the latest intake
// time that keeps bedtime levels under SLEEP_THRESHOLD_MG.
export default class extends Controller {
  static targets = [
    "drinkList", "drinkSelect", "drinkTime",
    "bedtime",
    "result", "empty",
    "totalCaffeine", "atBedtime", "bedtimeAdvice", "cutoffTime",
    "chart", "zoomToggle"
  ]

  static values = {
    halfLife: { type: Number, default: 5.0 },
    threshold: { type: Number, default: 50 }
  }

  connect() {
    this._drinks = []
    this._chart = null
    this._fullDay = false
  }

  toggleZoom() {
    this._fullDay = !this._fullDay
    if (this.hasZoomToggleTarget) {
      this.zoomToggleTarget.textContent = this._fullDay ? "Show focused view" : "Show full day"
    }
    this.render()
  }

  disconnect() {
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  addDrink() {
    const select = this.drinkSelectTarget
    const opt = select.options[select.selectedIndex]
    if (!opt) return

    const drink = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      slug: opt.value,
      label: opt.dataset.label,
      serving: opt.dataset.serving,
      caffeineMg: parseFloat(opt.dataset.caffeine),
      time: this.drinkTimeTarget.value || "08:00"
    }
    this._drinks.push(drink)
    this._sortDrinks()
    this._renderDrinkList()
    this.render()
  }

  removeDrink(event) {
    const id = event.currentTarget.dataset.id
    this._drinks = this._drinks.filter(d => d.id !== id)
    this._renderDrinkList()
    this.render()
  }

  // Re-renders results + chart. Called on bedtime change and after any
  // drink mutation.
  render() {
    if (this._drinks.length === 0) {
      this.resultTarget.classList.add("hidden")
      this.emptyTarget.classList.remove("hidden")
      return
    }
    this.emptyTarget.classList.add("hidden")
    this.resultTarget.classList.remove("hidden")

    const total = this._drinks.reduce((s, d) => s + d.caffeineMg, 0)
    this.totalCaffeineTarget.textContent = Math.round(total).toString()

    const bedtime = this._timeToHours(this.bedtimeTarget.value || "22:30")
    const atBedtime = this._levelAt(bedtime)
    this.atBedtimeTarget.textContent = Math.round(atBedtime).toString()

    if (atBedtime < this.thresholdValue) {
      this.bedtimeAdviceTarget.textContent =
        `Below the ${this.thresholdValue}mg sleep-friendly threshold.`
    } else {
      this.bedtimeAdviceTarget.textContent =
        `Above the ${this.thresholdValue}mg threshold — sleep onset may be affected.`
    }

    this.cutoffTimeTarget.textContent = this._cutoffTimeForBedtime(bedtime)
    this._renderChart(bedtime)
  }

  // ---- model ----

  // Caffeine in the bloodstream at a given hour (decimal, e.g. 22.5 = 22:30),
  // summed across all drinks. Drinks before that hour decay by their elapsed
  // time; drinks consumed after that hour contribute zero.
  _levelAt(hour) {
    return this._drinks.reduce((sum, d) => {
      const dt = hour - this._timeToHours(d.time)
      if (dt < 0) return sum
      return sum + d.caffeineMg * Math.pow(0.5, dt / this.halfLifeValue)
    }, 0)
  }

  // Latest intake clock-time such that bedtime caffeine equals threshold.
  // Computed against the *next* drink (last in the list) — we solve for
  // when that drink would have to be consumed for bedtime levels to land
  // exactly on the threshold, given everything else is fixed. If the
  // existing drinks already breach the threshold, the cutoff has passed.
  _cutoffTimeForBedtime(bedtime) {
    if (this._drinks.length === 0) return "—"

    const last = this._drinks[this._drinks.length - 1]
    const others = this._drinks.slice(0, -1)
    const fromOthers = others.reduce((sum, d) => {
      const dt = bedtime - this._timeToHours(d.time)
      if (dt < 0) return sum
      return sum + d.caffeineMg * Math.pow(0.5, dt / this.halfLifeValue)
    }, 0)

    const headroom = this.thresholdValue - fromOthers
    if (headroom <= 0) {
      return "Already past — switch to decaf."
    }

    // C0 * 0.5^(dt / hl) = headroom  →  dt = hl * log2(C0 / headroom)
    const ratio = last.caffeineMg / headroom
    if (ratio <= 1) {
      return "Anytime — your last drink is small enough."
    }
    const maxDt = this.halfLifeValue * Math.log2(ratio)
    const cutoffHour = bedtime - maxDt
    return this._hoursToTime(cutoffHour)
  }

  _renderDrinkList() {
    this.drinkListTarget.innerHTML = this._drinks.map(d => `
      <li class="flex items-center justify-between bg-amber-50 rounded-md px-3 py-2">
        <div>
          <span class="font-medium text-amber-900">${this._escape(d.label)}</span>
          <span class="text-sm text-amber-700">at ${this._escape(d.time)}</span>
          <span class="text-xs text-amber-600 ml-1">(${d.caffeineMg}mg)</span>
        </div>
        <button type="button" data-id="${d.id}"
                data-action="click->caffeine-clearance-calculator#removeDrink"
                class="text-red-700 hover:text-red-900 text-sm font-medium">
          Remove
        </button>
      </li>
    `).join("")
  }

  // ---- chart ----

  async _renderChart(bedtime) {
    if (!this.hasChartTarget) return
    // JSDOM (and other headless contexts) returns null from getContext.
    // Bail out instead of letting Chart.js leak memory trying.
    if (typeof this.chartTarget.getContext === "function" &&
        !this.chartTarget.getContext("2d")) return
    try {
      const { Chart, LineController, LineElement, PointElement,
              LinearScale, CategoryScale, Tooltip, Filler, Legend } = await import("chart.js")
      Chart.register(LineController, LineElement, PointElement, LinearScale,
                     CategoryScale, Tooltip, Filler, Legend)

      // Curve range: focused view starts at the earliest drink and ends
      // at bedtime + 2h (or earliest + 8h, whichever is later). Full-day
      // view ends at 06:00 next day (h=30) so the wrap-around shows
      // caffeine still in the bloodstream in the early hours, and starts
      // at the highest whole hour strictly before the first drink, capped
      // at 07:00 — skipping the "0 mg before any coffee" stretch which
      // isn't realistic anyway (people drink coffee the day before too).
      // Sampled every 30 minutes either way.
      let start, end
      const firstDrink = Math.min(...this._drinks.map(d => this._timeToHours(d.time)))
      if (this._fullDay) {
        start = this._fullDayStartHour(firstDrink)
        end = 30
      } else {
        start = Math.floor(firstDrink)
        end = Math.ceil(Math.max(bedtime + 2, start + 8))
      }
      const labels = []
      const data = []
      for (let h = start; h <= end; h += 0.5) {
        labels.push(h >= 24 ? `${this._hoursToTime(h)} (next day)` : this._hoursToTime(h))
        data.push(Math.max(0, this._levelAt(h)))
      }

      const thresholdLine = labels.map(() => this.thresholdValue)

      if (this._chart) this._chart.destroy()
      this._chart = new Chart(this.chartTarget, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Caffeine (mg)",
              data,
              borderColor: "#b45309",
              backgroundColor: "rgba(180, 83, 9, 0.15)",
              tension: 0.3,
              fill: true,
              pointRadius: 0
            },
            {
              label: `Sleep threshold (${this.thresholdValue}mg)`,
              data: thresholdLine,
              borderColor: "#dc2626",
              borderDash: [6, 4],
              borderWidth: 1.5,
              fill: false,
              pointRadius: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: true, position: "bottom" },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)}mg`
              }
            }
          },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: "Caffeine (mg)" } },
            x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } }
          }
        }
      })
    } catch {
      // Chart.js not available; fail silent — numeric summary still renders.
    }
  }

  // ---- helpers ----

  _sortDrinks() {
    this._drinks.sort((a, b) => this._timeToHours(a.time) - this._timeToHours(b.time))
  }

  // Highest whole hour strictly before the first drink, capped at 07:00.
  // First drink at 06:30 or 07:00 → 6; at 07:30 → 7; at lunch → still 7.
  // Anchored above zero so we never plot negative hours.
  _fullDayStartHour(firstDrink) {
    return Math.max(0, Math.min(Math.ceil(firstDrink) - 1, 7))
  }

  _timeToHours(time) {
    const [h, m] = (time || "00:00").split(":").map(n => parseInt(n, 10))
    return h + (m / 60)
  }

  _hoursToTime(hours) {
    let h = Math.floor(hours)
    let m = Math.round((hours - h) * 60)
    if (m === 60) { m = 0; h += 1 }
    if (h < 0) h += 24
    if (h >= 24) h -= 24
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`
  }

  _escape(s) {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }
}
